import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { BarChart3, Check, X, Building2, Loader2, ArrowLeft, AlertCircle, TrendingUp, Award } from "lucide-react";

// Friendly labels for tool ids surfacing in the coverage matrix
const TOOL_LABELS = {
  staffing_assessment: "Staffing Assessment",
  vsm_builder: "VSM",
  five_s: "5S Audit",
  kaizen: "Kaizen Charter",
  sipoc: "SIPOC",
  voc_kano: "VoC + Kano",
  process_observation: "Time Study",
  waste_walk: "Waste Walk",
  rca_5whys: "5 Whys / Fishbone",
  pareto: "Pareto",
  maturity: "Maturity",
  spaghetti: "Spaghetti",
  pfmea: "PFMEA",
  a3: "A3",
  dmaic: "DMAIC",
  yamazumi: "Yamazumi",
  impact_effort: "Impact × Effort",
  lean_wave: "Lean Wave",
  roi_calculator: "ROI",
  idea_bank: "Idea Bank",
  standard_work: "Standard Work",
  poka_yoke: "Poka-Yoke",
  kanban_designer: "Kanban Designer",
  smed: "SMED",
  kpi_scorecard: "KPI Scorecard",
  tier_meeting: "Tier Meetings",
  leader_standard_work: "Leader Std Work",
  lpa_scheduler: "LPA Scheduler",
  benefit_tracker: "Benefit Tracker",
  iluo_matrix: "ILUO Matrix",
  tna: "TNA",
  adkar: "ADKAR",
  lean_academy: "Lean Academy",
  erlang_c: "Erlang C",
  monte_carlo_queue: "Monte Carlo",
  attrition_cohort: "Attrition Cohort",
  automation_scorer: "Automation Scorer",
  customer_journey: "Customer Journey",
  dilo: "DILO",
};

const niceTool = (id) => TOOL_LABELS[id] || id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Aggregate KPIs across companies for the "Signature KPI" section.
// Returns { headline, unit: [{ company_name, value }] } grouped by KPI headline.
function groupKpis(rows) {
  const grouped = {};
  rows.forEach((r) => {
    (r.kpis || []).forEach((k) => {
      const key = `${k.headline}__${k.unit || ""}`;
      if (!grouped[key]) grouped[key] = { headline: k.headline, unit: k.unit, points: [] };
      grouped[key].points.push({ company_name: r.company_name, value: k.value, project_name: k.name });
    });
  });
  return Object.values(grouped);
}

export default function BenchmarkPage() {
  const navigate = useNavigate();
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/benchmark/eligible-companies");
        setEligible(r.data);
      } catch (e) {
        setError(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 8) next.add(id);
      return next;
    });
  };

  const run = async () => {
    setError("");
    if (selected.size < 2) return setError("Select at least two companies to benchmark");
    setRunning(true);
    try {
      const r = await api.post("/benchmark", { company_ids: Array.from(selected) });
      setResult(r.data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setSelected(new Set());
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button
          data-testid="benchmark-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm text-sage-500 hover:text-sage-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portfolio
        </button>
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Cross-Company Insight</p>
            <h1 className="font-heading text-3xl font-semibold text-sage-900 flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-sage-600" /> Benchmark Companies
            </h1>
            <p className="text-sage-500 text-sm mt-1">
              Compare tool coverage, journey progress and signature KPIs side-by-side. Pick 2–8 companies.
            </p>
          </div>
          {result && (
            <button
              data-testid="benchmark-reset"
              onClick={reset}
              className="border border-sage-200 text-sage-700 rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-50 transition-colors"
            >
              New benchmark
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 mb-6 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Company picker — hidden once results render */}
        {!result && (
          <section data-testid="benchmark-picker" className="bg-white border border-[#E2E8E3] rounded-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-base font-semibold text-sage-900">Select companies ({selected.size}/8)</h2>
              <button
                data-testid="benchmark-run"
                onClick={run}
                disabled={running || selected.size < 2}
                className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 disabled:opacity-50 transition-colors"
              >
                {running ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Running…
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4" /> Run Benchmark
                  </>
                )}
              </button>
            </div>
            {loading ? (
              <div className="py-12 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-sage-500" />
              </div>
            ) : eligible.length < 2 ? (
              <div className="py-10 text-center text-sage-500 text-sm">
                You need at least two companies to run a benchmark.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {eligible.map((c) => {
                  const isOn = selected.has(c.id);
                  return (
                    <button
                      key={c.id}
                      data-testid={`benchmark-pick-${c.id}`}
                      onClick={() => toggle(c.id)}
                      className={`text-left border rounded-md p-3 transition-all ${
                        isOn ? "border-sage-500 bg-sage-50" : "border-sage-200 bg-white hover:border-sage-300"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${isOn ? "bg-sage-600 border-sage-600" : "border-sage-300"}`}>
                            {isOn && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-sage-900 truncate">{c.name}</p>
                            <p className="text-xs text-sage-500 truncate">{c.domain || "—"} · {c.country || "—"}</p>
                          </div>
                        </div>
                        <span className="text-xs text-sage-400 whitespace-nowrap">{c.project_count} proj</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Results */}
        {result && (
          <div data-testid="benchmark-results" className="space-y-6">
            {/* KPI Summary Table */}
            <section className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
              <div className="px-6 py-4 border-b border-sage-100 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-sage-600" />
                <h2 className="font-heading text-base font-semibold text-sage-900">Portfolio Summary</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sage-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Company</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Domain</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Projects</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Completed</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Avg Completion</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Tools Used</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">Journey</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.company_id} data-testid={`benchmark-row-${r.company_id}`} className="border-t border-sage-100 hover:bg-sage-50/50">
                        <td className="px-4 py-3 font-medium text-sage-900 flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-sage-400" />
                          {r.company_name}
                        </td>
                        <td className="px-4 py-3 text-sage-600 text-xs">{r.domain || "—"}</td>
                        <td className="px-4 py-3 text-right">{r.project_count}</td>
                        <td className="px-4 py-3 text-right">{r.completed}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-sage-100 rounded-full overflow-hidden">
                              <div className="h-full bg-sage-500" style={{ width: `${r.avg_completion_pct}%` }} />
                            </div>
                            <span className="text-xs">{r.avg_completion_pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{r.tool_count}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-sage-100 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500" style={{ width: `${r.journey_pct}%` }} />
                            </div>
                            <span className="text-xs">{r.journey_pct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Tool Coverage Matrix */}
            <section className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
              <div className="px-6 py-4 border-b border-sage-100">
                <h2 className="font-heading text-base font-semibold text-sage-900">Tool Coverage Matrix</h2>
                <p className="text-xs text-sage-500 mt-0.5">Which tools each company has touched. Empty cells = whitespace opportunities.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sage-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-sage-600 font-semibold sticky left-0 bg-sage-50">Tool</th>
                      {result.rows.map((r) => (
                        <th key={r.company_id} className="px-3 py-2.5 text-xs text-sage-600 font-semibold whitespace-nowrap">{r.company_name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(result.coverage_matrix).length === 0 ? (
                      <tr><td colSpan={result.rows.length + 1} className="text-center py-6 text-sage-400 text-xs">No tools used yet across selected companies.</td></tr>
                    ) : (
                      Object.entries(result.coverage_matrix).map(([tool, hits]) => (
                        <tr key={tool} className="border-t border-sage-100">
                          <td className="px-4 py-2 sticky left-0 bg-white text-sage-700">{niceTool(tool)}</td>
                          {hits.map((on, i) => (
                            <td key={i} className="px-3 py-2 text-center">
                              {on ? (
                                <Check className="w-4 h-4 text-sage-600 mx-auto" strokeWidth={3} />
                              ) : (
                                <X className="w-3.5 h-3.5 text-sage-200 mx-auto" />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Signature KPIs */}
            {(() => {
              const groups = groupKpis(result.rows);
              if (groups.length === 0) return null;
              return (
                <section className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-sage-100 flex items-center gap-2">
                    <Award className="w-4 h-4 text-sage-600" />
                    <h2 className="font-heading text-base font-semibold text-sage-900">Signature KPIs</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                    {groups.map((g) => {
                      const max = Math.max(...g.points.map((p) => Number(p.value) || 0), 1);
                      return (
                        <div key={g.headline + g.unit} className="border border-sage-100 rounded-md p-4">
                          <p className="text-xs uppercase tracking-wider text-sage-500 font-semibold mb-2">
                            {g.headline} <span className="text-sage-400">{g.unit && `(${g.unit})`}</span>
                          </p>
                          <div className="space-y-2">
                            {g.points.map((pt, i) => (
                              <div key={i} className="text-xs">
                                <div className="flex justify-between mb-1">
                                  <span className="text-sage-700 truncate pr-2">{pt.company_name}</span>
                                  <span className="font-semibold text-sage-900">{Number(pt.value).toLocaleString()}</span>
                                </div>
                                <div className="h-1.5 bg-sage-50 rounded-full overflow-hidden">
                                  <div className="h-full bg-sage-500" style={{ width: `${(Number(pt.value) / max) * 100}%` }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
}
